import classNames from 'classnames';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import sortBy from 'lodash/sortBy';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { playButtonClickSound } from '@wg/web2clientapi/sound';
import { ErrorLoad, Link, StepCSSTransitionGroup } from '@wg/wows-react-uikit';

import { root as globalRoot } from '~/preloaded';
import { SEASON_TYPES } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { getLocalizationSafe, t } from '~/helpers/localization';
import { isQualifiedByBestSeasonTeam } from '~/helpers/seasons';
import useMount from '~/hooks/useMount';
import { ROUTES_MAP } from '~/router/routes';

import { AnimatedFetchingComponent, ClansRatingSearchResult } from '~/UIKit';
import ClansRatingData from '~/UIKit/ClansRating/ClanRatingData';
import RatingTableNav from '~/UIKit/ClansRating/RatingTabNav';
import SeasonsMenu from '~/UIKit/ClansRating/SeasonsMenu';
import { ClanSearchInput } from '~/UIKit/components';

import styles from './ClansRating.scss';

import type { NavigateFunction, Params } from 'react-router-dom';
import type { WowsLadderType } from '~/Actions/ActionClanProfile';
import type { LeagueDivisionRatingType, ToggleRatingListType } from '~/Actions/ActionClanRating';
import type { ISearchState } from '~/Reducers/ReducerSearch';
import type { AutocompleteState } from '~/store/slices/autocompleteSlice';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { ILadderSeasonLeague } from '~/store/slices/settingsSlice';
import type { IClanData, LeagueItem } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

const setLeaguesFromSettings = (leagues: Record<string, ILadderSeasonLeague>, hasError?: boolean): LeagueItem[] => {
  if (hasError) {
    return [];
  }

  const list: LeagueItem[] = Object.keys(leagues).map((key) => {
    const league = leagues[key];
    return {
      content: getLocalizationSafe(league, 'mark_secondary') || getLocalizationSafe(league, 'mark'),
      divisions: league.divisions,
      value: league.number,
      icons: league.icons,
      range: league.range,
    };
  });

  return sortBy(list, ['value']);
};

const getLeagueDivision = (props: Pick<ClansRatingProps, 'currentLeague' | 'currentDivision' | 'leagues'>) => {
  let leagueNumber: number;

  if (typeof props.currentLeague === 'undefined') {
    const league = props.leagues[1];
    leagueNumber = league.number;
  } else {
    leagueNumber = props.currentLeague;
  }

  const league = props.leagues[leagueNumber];

  let divisionNumber: number;
  if (!props.currentDivision) {
    const lastDivision = league.divisions[league.divisions.length - 1];
    divisionNumber = lastDivision.number;
  } else {
    divisionNumber = props.currentDivision;
  }

  return { leagueNumber, divisionNumber };
};

interface ClansRatingProps {
  urlParams: Readonly<Params<string>>;
  navigate: NavigateFunction;
  hasError?: boolean;

  autocomplete: AutocompleteState;
  currentAccount: ICurrentAccountState;

  clanId?: Nullable<number>;
  currentClanId?: number;

  wowsLadder: Nullable<WowsLadderType>;
  leagues: Record<string, ILadderSeasonLeague>;

  seasonType?: SEASON_TYPES;
  currentRealm: string;
  currentLeague?: number;
  currentDivision?: number;

  currentSeasonType?: SEASON_TYPES;
  isCrossRealmLadder?: boolean;
  currentSeason: number;

  ratingLists: ToggleRatingListType;
  isError?: boolean;
  isFetching?: boolean;
  errorMessage?: string;

  searchResult: ISearchState;
  root: string;

  clanHomeRealm?: string;
  clanBattleRealm?: Nullable<string>;

  setCurrentClanId: (clanId: number) => void;
  clearState: () => void;

  onAutocompleteItemClick: (item: IClanData, accountId: number) => void;
  onCrossRealmChange?: (flag: boolean) => void;

  onDivisionChange: (division: number, ladderIsUpdate?: boolean) => void;
  onLeagueAndDivisionChange: (params: LeagueDivisionRatingType) => void;
  onLeagueChange: (league: number) => void;

  onPageChanged: (page: number, abortSignal?: AbortSignal) => Promise<void>;
  onReloadClick: (abortSignal?: AbortSignal) => Promise<void>;

  onRealmChange: (realm: string) => void;
  onRender: (clanId: number) => void;

  onSearchInputChange: (term: string) => Promise<void>;
  onSeasonChange: (season: number) => void;
  onSeasonTypeChange: (type: SEASON_TYPES) => void;
}

const ClansRating = (props: ClansRatingProps) => {
  const {
    urlParams,
    navigate,
    hasError,
    currentAccount,
    clanId,
    currentClanId,
    wowsLadder,
    leagues: leaguesProp,
    seasonType,
    currentRealm,
    currentLeague,
    currentDivision,
    currentSeasonType,
    isCrossRealmLadder = false,
    currentSeason,
    ratingLists,
    isFetching = false,
    errorMessage,
    isError,
    searchResult,
    root: rootProp,
    clanHomeRealm,
    clanBattleRealm,
  } = props;

  const leagues = useMemo(() => setLeaguesFromSettings(leaguesProp, hasError), [leaguesProp, hasError]);

  const [removeSearchState, setRemoveSearchState] = useState(true);
  const [goToClan, setGoToClan] = useState(false);

  const [localSeasonType, setLocalSeasonType] = useState<SEASON_TYPES | undefined>(undefined);
  const [localSeason, setLocalSeason] = useState<number>(1);
  const [localRealm, setLocalRealm] = useState<string>('');

  const isBrawl = localSeasonType === SEASON_TYPES.BRAWL || props.currentSeasonType === SEASON_TYPES.BRAWL;

  const searchAbortRef = useRef<AbortController | null>(null);

  const abortSearch = useCallback(() => {
    searchAbortRef.current?.abort();
    searchAbortRef.current = null;
  }, []);

  const runAbortable = useCallback(
    async (fn: (signal: AbortSignal) => Promise<void>) => {
      abortSearch();
      const controller = new AbortController();
      searchAbortRef.current = controller;

      try {
        await fn(controller.signal);
      } finally {
        if (searchAbortRef.current === controller) {
          searchAbortRef.current = null;
        }
      }
    },
    [abortSearch],
  );

  const goToClanRef = useRef(goToClan);
  useEffect(() => {
    goToClanRef.current = goToClan;
  }, [goToClan]);

  const clanIdRef = useRef(clanId);
  useEffect(() => {
    clanIdRef.current = clanId;
  }, [clanId]);

  useMount(() => {
    dwhExport.push(DWH_EVENTS.CLAN_BATTLES.VIEW_CLAN_RATING);

    if (clanId && isUndefined(wowsLadder)) {
      props.onRender(clanId);
    } else if (wowsLadder) {
      const ladderLeague = wowsLadder.league;
      const ladderDivision = wowsLadder.division;

      props.onLeagueAndDivisionChange({
        league: currentLeague || ladderLeague,
        division: currentDivision || ladderDivision,
        clanId: currentClanId ? currentClanId : clanId ?? undefined,
        withGlobalSpinner: false,
        ownClan: false,
      });
    } else {
      props.onLeagueAndDivisionChange({ withGlobalSpinner: false });
    }

    // componentWillUnmount
    return () => {
      if (!goToClanRef.current) {
        props.clearState();
        if (typeof clanIdRef.current === 'number') {
          props.setCurrentClanId(clanIdRef.current);
        }
      }
      abortSearch();
    };
  });

  // URL params sync
  const lastUrlKeyRef = useRef<string>('');

  useEffect(() => {
    const { seasonType: urlSeasonType, season: urlSeason, realm: urlRealm } = urlParams;

    const urlKey = `${urlSeasonType ?? ''}|${urlSeason ?? ''}|${urlRealm ?? ''}`;
    if (lastUrlKeyRef.current === urlKey) {
      return;
    }
    lastUrlKeyRef.current = urlKey;

    // ORDER is important
    if (urlSeason) {
      props.onSeasonChange(Number(urlSeason));
    }
    if (urlSeasonType) {
      props.onSeasonTypeChange(urlSeasonType as SEASON_TYPES);
    }
    if (urlRealm) {
      props.onRealmChange(String(urlRealm));
    }

    setLocalSeasonType((urlSeasonType ?? '') as SEASON_TYPES);
    setLocalSeason(parseInt(String(urlSeason || currentSeason || 1), 10));
    setLocalRealm(String(urlRealm || currentRealm || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.seasonType, urlParams.season, urlParams.realm]);

  const prevSnapshotRef = useRef({
    leaguesProp,
    wowsLadder,
    currentLeague,
    currentDivision,
  });

  useEffect(() => {
    const prev = prevSnapshotRef.current;

    if (!wowsLadder) {
      prevSnapshotRef.current = { leaguesProp, wowsLadder, currentLeague, currentDivision };
      return;
    }

    if (!prev.wowsLadder && wowsLadder) {
      props.onLeagueAndDivisionChange({ clanId: wowsLadder.id });
      prevSnapshotRef.current = { leaguesProp, wowsLadder, currentLeague, currentDivision };
      return;
    }

    if (isUndefined(prev.wowsLadder) && wowsLadder === null) {
      const nextLD = getLeagueDivision({ currentLeague, currentDivision, leagues: leaguesProp });
      props.onLeagueAndDivisionChange({ league: nextLD.leagueNumber, division: nextLD.divisionNumber });
      prevSnapshotRef.current = { leaguesProp, wowsLadder, currentLeague, currentDivision };
      return;
    }

    if (!currentLeague && !currentDivision) {
      prevSnapshotRef.current = { leaguesProp, wowsLadder, currentLeague, currentDivision };
      return;
    }

    const nextLD = getLeagueDivision({ currentLeague, currentDivision, leagues: leaguesProp });

    if (currentLeague !== nextLD.leagueNumber) {
      props.onLeagueChange(nextLD.leagueNumber);
    }
    if (currentDivision !== nextLD.divisionNumber) {
      props.onDivisionChange(nextLD.divisionNumber);
    }

    prevSnapshotRef.current = { leaguesProp, wowsLadder, currentLeague, currentDivision };
  }, [leaguesProp, wowsLadder, currentLeague, currentDivision, props]);

  // UI handlers
  const handleClearClick = useCallback(() => {
    abortSearch();
    props.clearState();
    setRemoveSearchState(true);
  }, [abortSearch, props]);

  const onLeagueAndDivisionChange = useCallback(
    (ratingType: LeagueDivisionRatingType = {}) => {
      props.onLeagueAndDivisionChange(ratingType);
      setRemoveSearchState(true);
    },
    [props],
  );

  const onAutocompleteItemClick = useCallback(
    (item: IClanData) => {
      onLeagueAndDivisionChange({
        ownClan: false,
        clanId: item.id,
        league: item.league,
        division: item.division,
      });
      setRemoveSearchState(true);
    },
    [onLeagueAndDivisionChange],
  );

  const onClanClick = useCallback(
    (nextClanId: number) => {
      setRemoveSearchState(false);
      setGoToClan(true);

      void playButtonClickSound();
      navigate(`${globalRoot}${ROUTES_MAP.CLAN_PROFILE.replace(':clanId', String(nextClanId))}`);
    },
    [navigate],
  );

  const onPageChanged = useCallback(
    (page: number) =>
      runAbortable((signal) => {
        setRemoveSearchState(false);
        return props.onPageChanged(page, signal);
      }),
    [runAbortable, props],
  );

  const onReloadClick = useCallback(
    () =>
      runAbortable((signal) => {
        setRemoveSearchState(false);
        return props.onReloadClick(signal);
      }),
    [runAbortable, props],
  );

  const showLeaders = useCallback(() => {
    void playButtonClickSound();
    onLeagueAndDivisionChange();
  }, [onLeagueAndDivisionChange]);

  const goToMyPosition = useCallback(() => {
    if (!wowsLadder) {
      return;
    }

    const seasonData = find(wowsLadder.ratings, {
      season_number: localSeason || currentSeason,
    });

    if (seasonData) {
      void playButtonClickSound();
      onLeagueAndDivisionChange({
        league: seasonData.league,
        division: seasonData.division,
        clanId: clanId ?? undefined,
      });
    }
  }, [wowsLadder, localSeason, currentSeason, onLeagueAndDivisionChange, clanId]);

  const onSeasonChange = useCallback(
    (nextSeason: number, seasonTypeArg: string | null | undefined) => {
      handleClearClick();

      props.onSeasonChange(nextSeason);
      setLocalSeason(nextSeason);

      let resolvedSeasonType = seasonTypeArg;
      if (seasonTypeArg === null || seasonTypeArg === undefined) {
        resolvedSeasonType = props.currentSeasonType;
      }

      navigate(`${rootProp}clan-battles/rating/${resolvedSeasonType}/${nextSeason}/${localRealm}`);
    },
    [handleClearClick, props, navigate, rootProp, localRealm],
  );

  const onRealmChange = useCallback(
    (realm: string) => {
      handleClearClick();

      props.onRealmChange(realm);
      setLocalRealm(realm);

      navigate(`${rootProp}clan-battles/rating/${props.currentSeasonType}/${localSeason}/${realm}`);
    },
    [handleClearClick, props, navigate, rootProp, localSeason],
  );

  // Render conditions
  if (hasError) {
    return (
      <StepCSSTransitionGroup level={1}>
        <ErrorLoad
          isFlat
          key="error-load"
          message={t('Произошла ошибка. Повторите попытку позже')}
          onReloadClick={() => {}}
        />
      </StepCSSTransitionGroup>
    );
  }

  if (!currentDivision) {
    return null;
  }

  const qualified = wowsLadder ? isQualifiedByBestSeasonTeam(wowsLadder.ratings, currentSeason, currentRealm) : false;
  const hideTableMenu = leagues.length === 1;

  return (
    <AnimatedFetchingComponent isFetching={isFetching} className={styles.container}>
      <StepCSSTransitionGroup level={1}>
        <SeasonsMenu
          clanBattleRealm={clanBattleRealm ?? undefined}
          clanHomeRealm={clanHomeRealm}
          currentRealm={currentRealm}
          currentSeason={localSeason || currentSeason}
          currentSeasonType={localSeasonType || currentSeasonType}
          isBrawl={isBrawl}
          onRealmChange={onRealmChange}
          onSeasonChange={onSeasonChange}
        />
      </StepCSSTransitionGroup>

      <StepCSSTransitionGroup level={2}>
        {!hideTableMenu && removeSearchState && (
          <RatingTableNav
            currentLeague={currentLeague}
            leagues={leagues}
            currentDivision={currentDivision}
            onDivisionChange={props.onDivisionChange}
            onLeagueChange={props.onLeagueChange}
          />
        )}

        <div className={styles.ratingTableNav}>
          <div>
            <Link isActionLink onClick={showLeaders} arrow={''}>
              {t('Лидеры сезона')}
            </Link>

            <div
              className={classNames(styles.clanPresent, {
                [styles.isShow]: qualified,
              })}
            >
              <Link isActionLink arrow="down" onClick={goToMyPosition}>
                {t('Перейти к позиции моего клана')}
              </Link>
            </div>

            <div
              className={classNames(styles.clanAbsent, {
                [styles.isShow]: !qualified && clanId,
              })}
            >
              {t('В выбранном регионе ваш клан отсутствует в рейтинге')}
            </div>
          </div>

          <div className={styles.searchInputWrap} data-staff-name="ratings_search-input">
            <ClanSearchInput
              key="search-input"
              searchType={'clansRating'}
              onHistoryItemClick={onAutocompleteItemClick}
              onSubmit={() => setRemoveSearchState(false)}
            />
          </div>
        </div>
      </StepCSSTransitionGroup>

      {!removeSearchState && (
        <StepCSSTransitionGroup level={3}>
          <ClansRatingSearchResult
            key="search-result"
            currentAccount={currentAccount}
            searchResult={searchResult}
            currentSeason={currentSeason}
            isCrossRealmLadder={isCrossRealmLadder}
            onItemClick={onLeagueAndDivisionChange}
            onPageChanged={onPageChanged}
            onReloadClick={onReloadClick}
          />
        </StepCSSTransitionGroup>
      )}

      {removeSearchState && (
        <ClansRatingData
          ratingLists={ratingLists}
          currentLeague={currentLeague}
          onLeagueChange={props.onLeagueChange}
          onClanClick={onClanClick}
          onDivisionChange={props.onDivisionChange}
          currentClanId={currentClanId}
          currentDivision={currentDivision}
          currentSeason={currentSeason}
          seasonType={seasonType}
          isCrossRealmLadder={isCrossRealmLadder}
          errorMessage={errorMessage}
          isError={isError}
          isFetching={isFetching}
        />
      )}
    </AnimatedFetchingComponent>
  );
};

export default memo(ClansRating);
